import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Table,
  Typography,
  Space,
  Row,
  Col,
  Statistic,
  Button,
  Spin,
  message,
  Empty,
  Tag,
  Select,
  Radio,
  Modal,
  Tabs,
  Alert
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { ReloadOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  fetchServiceItems,
  fetchExternalServiceItems
} from '../services/serviceOrderService';
import { useBreakpoint } from '../hooks/useBreakpoint';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const ITEM_TYPE_LABELS: Record<string, string> = {
  product: 'Ürün',
  service: 'Hizmet',
  externalService: 'Dış Hizmet'
};

const ITEM_TYPE_COLORS: Record<string, string> = {
  product: 'blue',
  service: 'green',
  externalService: 'orange'
};

interface DetailedProfitabilityRow {
  itemType: string;
  itemId?: number;
  itemName: string;
  quantity: number;
  salesAmount: number;
  costAmount: number;
  profit: number;
  margin: number;
}

interface DetailedProfitabilityResponse {
  rows: DetailedProfitabilityRow[];
  totals: {
    salesAmount: number;
    costAmount: number;
    profit: number;
    margin: number;
  };
  startDate?: string;
  endDate?: string;
}

interface DetailLineItem {
  id: number;
  movementDate: string;
  sourceType?: string;
  documentNo?: string | null;
  documentId?: number | null;
  serviceOrderId?: number | null;
  description?: string | null;
  warehouseName?: string | null;
  quantity: number;
  salesUnitPrice?: number | null;
  salesAmount?: number | null;
  purchaseUnitPrice?: number | null;
  unitCost?: number | null;
  costAmount?: number | null;
  zeroCost?: boolean;
  createdByName?: string | null;
}

interface DetailResponse {
  itemType: string;
  itemId: number;
  itemName?: string;
  tab: string;
  note?: string;
  page: number;
  pageSize: number;
  totalCount: number;
  items: DetailLineItem[];
}

const DEFAULT_RANGE_DAYS = 10;

type MainFilter = 'all' | 'service' | 'externalService' | 'product';

interface CategoryOption {
  id?: number;
  Id?: number;
  categoryName?: string;
  CategoryName?: string;
  parentId?: number;
  ParentId?: number;
}

const DetailedProfitabilityReport: React.FC = () => {
  const isMobile = useBreakpoint();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
    dayjs().endOf('day')
  ]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DetailedProfitabilityResponse | null>(null);
  const [mainFilter, setMainFilter] = useState<MainFilter>('all');
  const [serviceItemId, setServiceItemId] = useState<number>(0);
  const [externalServiceItemId, setExternalServiceItemId] = useState<number>(0);
  const [categoryId, setCategoryId] = useState<number>(0);
  const [serviceItems, setServiceItems] = useState<Array<{ id: number; name: string }>>([]);
  const [externalServiceItems, setExternalServiceItems] = useState<Array<{ id: number; name: string }>>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<DetailedProfitabilityRow | null>(null);
  const [detailTab, setDetailTab] = useState<'sales' | 'cost'>('sales');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<DetailResponse | null>(null);
  const [detailPage, setDetailPage] = useState({ current: 1, pageSize: 20 });

  const { baseCurrencySymbol } = useCurrency();
  const filterSelectStyle = isMobile ? { minWidth: 140, maxWidth: '100%' } : { minWidth: 220 };

  const formatCurrency = useMemo(
    () =>
      (value: number) =>
        new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: 'TRY',
          currencyDisplay: 'symbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value ?? 0).replace('TRY', baseCurrencySymbol || '₺'),
    [baseCurrencySymbol]
  );

  const formatMargin = (value: number) =>
    `${value?.toFixed(2) ?? '0.00'} %`;

  const loadOptions = useCallback(async () => {
    try {
      const [services, externals, catsRes] = await Promise.all([
        fetchServiceItems(true),
        fetchExternalServiceItems(true),
        api.get<CategoryOption[]>('/categories')
      ]);
      setServiceItems(services.map((s) => ({ id: s.id, name: s.name })));
      setExternalServiceItems(externals.map((e) => ({ id: e.id, name: e.name })));
      setCategories(catsRes.data || []);
    } catch (e) {
      console.error('Load filter options error:', e);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const loadData = useCallback(
    async (
      range: [Dayjs, Dayjs],
      filter: MainFilter,
      srvId: number,
      extId: number,
      catId: number
    ) => {
      if (!range || !range[0] || !range[1]) {
        return;
      }

      try {
        setLoading(true);

        const params: Record<string, string | undefined> = {
          startDate: range[0].startOf('day').toISOString(),
          endDate: range[1].endOf('day').toISOString(),
          itemTypeFilter: filter === 'all' ? 'all' : filter
        };
        if (filter === 'service' && typeof srvId === 'number' && srvId > 0) params.serviceItemId = String(srvId);
        if (filter === 'externalService' && typeof extId === 'number' && extId > 0) params.externalServiceItemId = String(extId);
        if (filter === 'product' && typeof catId === 'number' && catId > 0) params.categoryId = String(catId);

        const { data: res } = await api.get<DetailedProfitabilityResponse>(
          '/reports/detailedprofitability',
          { params }
        );
        setData(res);
      } catch (error: any) {
        console.error('Detailed profitability report load error:', error);
        message.error(
          error?.response?.data?.message || 'Detaylı kârlılık raporu yüklenemedi'
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData(dateRange, mainFilter, serviceItemId, externalServiceItemId, categoryId);
  }, [dateRange, mainFilter, serviceItemId, externalServiceItemId, categoryId, loadData]);

  const handleRangeChange = (values: null | [Dayjs | null, Dayjs | null]) => {
    if (!values || !values[0] || !values[1]) {
      setDateRange([
        dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
        dayjs().endOf('day')
      ]);
      return;
    }
    setDateRange([values[0], values[1]]);
  };

  const handleMainFilterChange = (v: MainFilter) => {
    setMainFilter(v);
    setServiceItemId(0);
    setExternalServiceItemId(0);
    setCategoryId(0);
  };

  const loadDetail = useCallback(
    async (
      row: DetailedProfitabilityRow,
      tab: 'sales' | 'cost',
      page = 1,
      pageSize = 20
    ) => {
      if (!row.itemId) {
        message.info('Bu satır için detay yok.');
        return;
      }
      try {
        setDetailLoading(true);
        const { data: res } = await api.get<DetailResponse>(
          '/reports/detailedprofitability/detail',
          {
            params: {
              itemType: row.itemType,
              itemId: row.itemId,
              tab,
              startDate: dateRange[0].startOf('day').toISOString(),
              endDate: dateRange[1].endOf('day').toISOString(),
              page,
              pageSize
            }
          }
        );
        setDetailData(res);
        setDetailPage({ current: res.page ?? page, pageSize: res.pageSize ?? pageSize });
      } catch (error: any) {
        message.error(error?.response?.data?.message || 'Detay yüklenemedi');
      } finally {
        setDetailLoading(false);
      }
    },
    [dateRange]
  );

  const openDetail = (row: DetailedProfitabilityRow) => {
    if (!row.itemId) {
      message.info('Bu satır için detay yok.');
      return;
    }
    setDetailRow(row);
    setDetailTab('sales');
    setDetailOpen(true);
    setDetailData(null);
    loadDetail(row, 'sales', 1, detailPage.pageSize);
  };

  const runLoad = () => {
    loadData(dateRange, mainFilter, serviceItemId, externalServiceItemId, categoryId);
  };

  const columns: ColumnsType<DetailedProfitabilityRow> = [
    {
      title: 'Kalem Türü',
      dataIndex: 'itemType',
      key: 'itemType',
      width: 120,
      render: (type: string) => (
        <Tag color={ITEM_TYPE_COLORS[type] ?? 'default'}>
          {ITEM_TYPE_LABELS[type] ?? type}
        </Tag>
      )
    },
    {
      title: 'Kalem Adı',
      dataIndex: 'itemName',
      key: 'itemName',
      render: (name: string, record: DetailedProfitabilityRow) =>
        name?.trim() ||
        (record.itemId ? `#${record.itemId}` : '-')
    },
    {
      title: 'Miktar',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      width: 100,
      render: (value: number) => (
        <Text>{typeof value === 'number' ? value.toFixed(2) : value}</Text>
      )
    },
    {
      title: 'Satış Tutarı',
      dataIndex: 'salesAmount',
      key: 'salesAmount',
      align: 'right',
      render: (value: number) => <Text>{formatCurrency(value)}</Text>
    },
    {
      title: 'Maliyet',
      dataIndex: 'costAmount',
      key: 'costAmount',
      align: 'right',
      render: (value: number) => <Text>{formatCurrency(value)}</Text>
    },
    {
      title: 'Kâr',
      dataIndex: 'profit',
      key: 'profit',
      align: 'right',
      render: (value: number) => (
        <Text type={value >= 0 ? 'success' : 'danger'}>
          {formatCurrency(value)}
        </Text>
      )
    },
    {
      title: 'Kâr Marjı',
      dataIndex: 'margin',
      key: 'margin',
      align: 'right',
      render: (value: number) => (
        <Text type={value >= 0 ? 'success' : 'danger'}>
          {formatMargin(value)}
        </Text>
      )
    }
  ];

  const totals = data?.totals;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Title level={3}>Detaylı Kârlılık Raporu</Title>
      <Text type="secondary">
        Ürünler, hizmetler ve dış hizmet kalemlerinin toplam kârlılığı
      </Text>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space wrap align="center">
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              allowClear={false}
              format="DD.MM.YYYY"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={runLoad}
              loading={loading}
            >
              Yenile
            </Button>
          </Space>

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong>Filtre</Text>
            <Space wrap align="center">
              <Radio.Group
                value={mainFilter}
                onChange={(e) => handleMainFilterChange(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="all">Tümü</Radio.Button>
                <Radio.Button value="service">Hizmet</Radio.Button>
                <Radio.Button value="externalService">Dış Hizmet</Radio.Button>
                <Radio.Button value="product">Ürünler</Radio.Button>
              </Radio.Group>
              {mainFilter === 'service' && (
                <Select
                  placeholder="Hizmet seçin"
                  style={filterSelectStyle}
                  value={serviceItemId}
                  onChange={(v) => setServiceItemId(v ?? 0)}
                  options={[
                    { label: 'Tüm hizmetler', value: 0 },
                    ...serviceItems.map((s) => ({ label: s.name, value: s.id }))
                  ]}
                />
              )}
              {mainFilter === 'externalService' && (
                <Select
                  placeholder="Dış hizmet seçin"
                  style={filterSelectStyle}
                  value={externalServiceItemId}
                  onChange={(v) => setExternalServiceItemId(v ?? 0)}
                  options={[
                    { label: 'Tüm dış hizmetler', value: 0 },
                    ...externalServiceItems.map((e) => ({ label: e.name, value: e.id }))
                  ]}
                />
              )}
              {mainFilter === 'product' && (
                <Select
                  placeholder="Kategori seçin"
                  style={filterSelectStyle}
                  value={categoryId}
                  onChange={(v) => setCategoryId(typeof v === 'number' ? v : 0)}
                  options={[
                    { label: 'Tüm ürünler', value: 0 },
                    ...categories.map((c) => ({
                      label: c.categoryName ?? c.CategoryName ?? '',
                      value: c.id ?? c.Id ?? 0
                    })).filter((o) => o.value > 0)
                  ]}
                />
              )}
            </Space>
          </Space>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Toplam Satış"
                  value={totals?.salesAmount ?? 0}
                  precision={2}
                  prefix={baseCurrencySymbol}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Toplam Maliyet"
                  value={totals?.costAmount ?? 0}
                  precision={2}
                  prefix={baseCurrencySymbol}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Toplam Kâr"
                  value={totals?.profit ?? 0}
                  precision={2}
                  prefix={baseCurrencySymbol}
                  valueStyle={{
                    color: (totals?.profit ?? 0) >= 0 ? '#3f8600' : '#cf1322'
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered>
                <Statistic
                  title="Ortalama Kâr Marjı"
                  value={totals?.margin ?? 0}
                  precision={2}
                  suffix="%"
                  valueStyle={{
                    color: (totals?.margin ?? 0) >= 0 ? '#3f8600' : '#cf1322'
                  }}
                />
              </Card>
            </Col>
          </Row>
        </Space>
      </Card>

      <Card>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Satıra tıklayarak satış ve maliyet dökümünü görüntüleyin. Kullanılan maliyet 0 ise kâr %100 görünür.
        </Text>
        <Spin spinning={loading}>
          {data?.rows?.length ? (
            <Table<DetailedProfitabilityRow>
              dataSource={data.rows}
              columns={columns}
              rowKey={(record) =>
                `${record.itemType}-${record.itemId ?? 0}-${record.itemName || ''}`
              }
              pagination={false}
              scroll={{ x: 750 }}
              onRow={(record) => ({
                onClick: () => openDetail(record),
                style: { cursor: record.itemId ? 'pointer' : undefined }
              })}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Toplam</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>{formatCurrency(totals?.salesAmount ?? 0)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <Text strong>{formatCurrency(totals?.costAmount ?? 0)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <Text
                      strong
                      type={(totals?.profit ?? 0) >= 0 ? 'success' : 'danger'}
                    >
                      {formatCurrency(totals?.profit ?? 0)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <Text
                      strong
                      type={(totals?.margin ?? 0) >= 0 ? 'success' : 'danger'}
                    >
                      {formatMargin(totals?.margin ?? 0)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          ) : (
            <Empty description="Seçilen tarih aralığı için veri bulunamadı" />
          )}
        </Spin>
      </Card>

      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>
            Kapat
          </Button>
        ]}
        title={
          detailRow
            ? `${ITEM_TYPE_LABELS[detailRow.itemType] ?? detailRow.itemType}: ${
                detailRow.itemName?.trim() || `#${detailRow.itemId}`
              }`
            : 'Kalem detayı'
        }
        width={980}
        destroyOnClose
      >
        {detailRow && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              Satış {formatCurrency(detailRow.salesAmount)} · Maliyet{' '}
              {formatCurrency(detailRow.costAmount)} · Kâr {formatCurrency(detailRow.profit)} (
              {formatMargin(detailRow.margin)})
            </Text>
            <Tabs
              activeKey={detailTab}
              onChange={(key) => {
                const tab = key as 'sales' | 'cost';
                setDetailTab(tab);
                if (detailRow) loadDetail(detailRow, tab, 1, detailPage.pageSize);
              }}
              items={[
                { key: 'sales', label: 'Satış satırları' },
                { key: 'cost', label: 'Maliyet / alış girişleri' }
              ]}
            />
            {detailData?.note ? (
              <Alert type="info" showIcon message={detailData.note} />
            ) : null}
            <Spin spinning={detailLoading}>
              <Table<DetailLineItem>
                size="small"
                rowKey={(r) => String(r.id)}
                dataSource={detailData?.items ?? []}
                scroll={{ x: 900 }}
                pagination={{
                  current: detailPage.current,
                  pageSize: detailPage.pageSize,
                  total: detailData?.totalCount ?? 0,
                  showSizeChanger: true,
                  showTotal: (t) => `Toplam ${t}`,
                  onChange: (page, pageSize) => {
                    if (detailRow) loadDetail(detailRow, detailTab, page, pageSize);
                  }
                }}
                columns={
                  detailTab === 'sales'
                    ? [
                        {
                          title: 'Tarih',
                          dataIndex: 'movementDate',
                          width: 140,
                          render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm')
                        },
                        {
                          title: 'Kaynak',
                          dataIndex: 'sourceType',
                          width: 110
                        },
                        {
                          title: 'Fiş No',
                          dataIndex: 'documentNo',
                          width: 130,
                          render: (v?: string | null) => v || '—'
                        },
                        {
                          title: 'Açıklama',
                          dataIndex: 'description',
                          ellipsis: true,
                          render: (v?: string | null) => v || '—'
                        },
                        {
                          title: 'Depo',
                          dataIndex: 'warehouseName',
                          width: 100,
                          render: (v?: string | null) => v || '—'
                        },
                        {
                          title: 'Miktar',
                          dataIndex: 'quantity',
                          width: 80,
                          align: 'right'
                        },
                        {
                          title: 'Satış B.Fiyat',
                          dataIndex: 'salesUnitPrice',
                          width: 110,
                          align: 'right',
                          render: (v?: number | null) =>
                            v != null ? formatCurrency(v) : '—'
                        },
                        {
                          title: 'Satış Tutar',
                          dataIndex: 'salesAmount',
                          width: 110,
                          align: 'right',
                          render: (v?: number | null) =>
                            v != null ? formatCurrency(v) : '—'
                        },
                        {
                          title: 'Kullanılan Maliyet',
                          dataIndex: 'unitCost',
                          width: 130,
                          align: 'right',
                          render: (v: number | undefined, r: DetailLineItem) => (
                            <Text type={r.zeroCost ? 'danger' : undefined}>
                              {v != null ? formatCurrency(v) : '—'}
                              {r.zeroCost ? ' (0)' : ''}
                            </Text>
                          )
                        },
                        {
                          title: 'Maliyet Tutar',
                          dataIndex: 'costAmount',
                          width: 110,
                          align: 'right',
                          render: (v?: number | null) =>
                            v != null ? formatCurrency(v) : '—'
                        },
                        {
                          title: 'İşlemi Yapan',
                          dataIndex: 'createdByName',
                          width: 120,
                          render: (v?: string | null) => v || '—'
                        }
                      ]
                    : [
                        {
                          title: 'Tarih',
                          dataIndex: 'movementDate',
                          width: 140,
                          render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm')
                        },
                        {
                          title: 'Kaynak',
                          dataIndex: 'sourceType',
                          width: 120
                        },
                        {
                          title: 'Fiş No',
                          dataIndex: 'documentNo',
                          width: 130,
                          render: (v?: string | null) => v || '—'
                        },
                        {
                          title: 'Açıklama',
                          dataIndex: 'description',
                          ellipsis: true,
                          render: (v?: string | null) => v || '—'
                        },
                        {
                          title: 'Depo',
                          dataIndex: 'warehouseName',
                          width: 100,
                          render: (v?: string | null) => v || '—'
                        },
                        {
                          title: 'Miktar',
                          dataIndex: 'quantity',
                          width: 80,
                          align: 'right'
                        },
                        {
                          title: 'Alış B.Fiyat',
                          dataIndex: 'purchaseUnitPrice',
                          width: 110,
                          align: 'right',
                          render: (v?: number | null, r?: DetailLineItem) => {
                            if (v != null) return formatCurrency(v);
                            if (r?.unitCost != null) return formatCurrency(r.unitCost);
                            return '—';
                          }
                        },
                        {
                          title: 'Birim Maliyet',
                          dataIndex: 'unitCost',
                          width: 120,
                          align: 'right',
                          render: (v: number | undefined, r: DetailLineItem) => (
                            <Text type={r.zeroCost ? 'danger' : undefined}>
                              {v != null ? formatCurrency(v) : '—'}
                              {r.zeroCost ? ' (0)' : ''}
                            </Text>
                          )
                        },
                        {
                          title: 'Maliyet Tutar',
                          dataIndex: 'costAmount',
                          width: 110,
                          align: 'right',
                          render: (v?: number | null) =>
                            v != null ? formatCurrency(v) : '—'
                        },
                        {
                          title: 'İşlemi Yapan',
                          dataIndex: 'createdByName',
                          width: 120,
                          render: (v?: string | null) => v || '—'
                        }
                      ]
                }
                locale={{ emptyText: 'Kayıt bulunamadı' }}
              />
            </Spin>
          </Space>
        )}
      </Modal>
    </Space>
  );
};

export default DetailedProfitabilityReport;
